import React from 'react';
import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
  useTheme,
} from '@mui/material';
import { Loader2 } from 'lucide-react';

interface CustomButtonProps extends MuiButtonProps {
  customVariant?: 'default' | 'border' | 'dark' | 'light';
  fullWidth?: boolean;
  loading?: boolean;
}

const Button: React.FC<CustomButtonProps> = ({
  customVariant = 'default',
  fullWidth = false,
  loading = false,
  children,
  onClick,
  ...props
}) => {
  const theme = useTheme();

  const variantStyles = (() => {
    switch (customVariant) {
      case 'border':
        return {
          backgroundColor: theme.palette.green.main,
          border: '1px solid black',
          borderRadius: '15px',
          '&:hover': {
            backgroundColor: theme.palette.green.main,
            filter: 'brightness(0.95)',
          },
          color: 'black',
        };
      case 'dark':
        return {
          backgroundColor: theme.palette.green.dark,
          '&:hover': {
            backgroundColor: theme.palette.green.dark,
            filter: 'brightness(0.95)',
          },
          color: 'black',
        };
      case 'light':
        return {
          backgroundColor: '#FFF',
          border: '0.1px solid black',
          borderRadius: '15px',
          '&:hover': {
            backgroundColor: '#FFF',
            filter: 'brightness(0.95)',
          },
          color: 'black',
        };

      default:
        return {
          backgroundColor: '#B6F829',
          borderRadius: '15px',
          '&:hover': {
            backgroundColor: '#B6F829',
            filter: 'brightness(0.95)',
          },
          color: 'black',
        };
    }
  })();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!loading && onClick) {
      onClick(event);
    }
  };

  return (
    <MuiButton
      {...props}
      onClick={handleClick}
      disabled={loading || props.disabled}
      sx={{
        py: '5px',
        flexShrink: 0,
        px: '25px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        borderRadius: '7px',
        width: fullWidth ? '100%' : 'auto',
        textTransform: 'none',
        fontFamily: 'AlmoniDl',
        fontWeight: '700',
        fontSize: 16,
        ...variantStyles,
      }}
    >
      {loading ? (
        <Loader2
          style={{
            height: '25px',
            width: '25px',
            animation: 'spin 1s linear infinite',
          }}
        />
      ) : (
        children
      )}
    </MuiButton>
  );
};

export default Button;
